import { memo, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { MainLayout } from '@/app/layouts';

import { initAppData } from '@/features/app-data';
import { ClarityIdentify } from '@/features/clarity-identify';
import { OnboardingGuard } from '@/features/guard';

import { useSessionIsAuthorized } from '@/entities/auth';
import { useViewerDataStatus, viewerReducer } from '@/entities/viewer';

import { getSignInPath } from '@/shared/constants/router';
import { MetaPixelPageTracker } from '@/shared/lib/fbpixel';
import { useAppDispatch, useDynamicReducersLoader } from '@/shared/lib/store';

const reducers = { viewer: viewerReducer };

const REDIRECT_ALLOWLIST = ['/checkout/boost'];

const buildSignInRedirect = (pathname: string, search: string): string => {
    const target = `${pathname}${search}`;

    if (!target.startsWith('/')) return getSignInPath();

    const isAllowed = REDIRECT_ALLOWLIST.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );

    return isAllowed
        ? `${getSignInPath()}?redirect=${encodeURIComponent(target)}`
        : getSignInPath();
};

export const PrivateRootElement = memo(() => {
    const dispatch = useAppDispatch();
    const isAuthorized = useSessionIsAuthorized();
    const status = useViewerDataStatus();
    const location = useLocation();

    useDynamicReducersLoader({ reducers, needRemoveAfterUnmount: false });

    useEffect(() => {
        if (isAuthorized) {
            dispatch(initAppData());
        }
    }, [isAuthorized, dispatch]);

    if (!isAuthorized) {
        return (
            <Navigate
                replace
                to={buildSignInRedirect(location.pathname, location.search)}
            />
        );
    }

    return (
        <>
            <ClarityIdentify />
            <MetaPixelPageTracker />
            <OnboardingGuard>
                <MainLayout isLoadingUserData={status !== 'success'} />
            </OnboardingGuard>
        </>
    );
});

PrivateRootElement.displayName = 'PrivateRootElement';
